import { FC, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { ScheduleEntry } from "@models/ScheduleEntry.model";
import { ScheduleSegment } from "@models/ScheduleSegment.model";
import { ScheduleService } from "@services/schedule/ScheduleService";
import { DateRangePicker } from "@components/DatePicker/DateRangePicker";
import socialMediaData from "src/shared/data/socialMediaData";
import {
  barGeometry,
  campaignWindow,
  monthBoundaries,
  dayCount,
  dayStart,
  formatRange,
  inheritsParent,
  rangesOf,
  typeLabel,
  type Range,
  type Window,
} from "./scheduleTree";

const service = new ScheduleService();

type LocalRange = Range & { id: number; key: number };

let keySeed = 1;
const toLocal = (range: Range, id: number): LocalRange => ({ ...range, id, key: keySeed++ });

const networkOf = (subtype: string | null | undefined) =>
  subtype ? socialMediaData[subtype.toLowerCase()] : undefined;

const networkLabel = (subtype: string | null | undefined) =>
  networkOf(subtype)?.label ?? subtype ?? "";

interface Props {
  /** The row that was opened. Its platform overrides become switcher targets. */
  entry: ScheduleEntry;
  parent: ScheduleEntry | null;
  overrides: readonly ScheduleEntry[];
  /** Networks enabled on this campaign — the only overrides that can be added. */
  availableNetworks: readonly string[];
  window: Window;
  onClose: () => void;
  onSaved: () => void;
}

/*
    The one place dates change. A switcher across the top picks which entry the
    calendar is editing — the row itself, or one of its platform overrides —
    so an override is always created from inside the thing it overrides.
*/
const ScheduleEntryModal: FC<Props> = ({
  entry,
  parent,
  overrides,
  availableNetworks,
  window: campaign,
  onClose,
  onSaved,
}) => {
  const [targetId, setTargetId] = useState<number>(entry.id);
  const [localOverrides, setLocalOverrides] = useState<ScheduleEntry[]>([...overrides]);
  const [addOpen, setAddOpen] = useState(false);
  /*
      Adding or removing an override writes immediately, but reloading the list
      underneath while the modal is open would tear it down mid-edit. Defer the
      refresh to close.
  */
  const [listIsStale, setListIsStale] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const target = targetId === entry.id ? entry : localOverrides.find((o) => o.id === targetId) ?? entry;
  const targetParent = targetId === entry.id ? parent : entry;

  // A platform override is a leaf: it modifies one row for one network, so it
  // has nothing of its own to override and the switcher is meaningless on it.
  const openedAnOverride = !!entry.subtype;
  const targetIsOverride = !!target.subtype;

  const [ranges, setRanges] = useState<LocalRange[]>([]);
  const [deletedSegmentIds, setDeletedSegmentIds] = useState<number[]>([]);
  const [isInheriting, setIsInheriting] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Switching target swaps the whole editing context.
  useEffect(() => {
    setRanges(rangesOf(target).map((r, i) => toLocal(r, target.segments?.[i]?.id ?? -1)));
    setDeletedSegmentIds([]);
    setIsInheriting(inheritsParent(target, targetParent));
    setActiveIndex(0);
    setConfirmRemove(false);
    setError(null);
  }, [targetId]);

  /*
      A child can never run outside its parent, so the picker is clamped rather
      than validated after the fact.
  */
  const bounds = useMemo(
    () =>
      targetParent
        ? { min: dayStart(targetParent.start_time), max: dayStart(targetParent.end_time) }
        : { min: undefined, max: undefined },
    [targetParent],
  );

  const active = ranges[activeIndex];

  const setActiveRange = (start: Date | null, end: Date | null) => {
    if (!start) return;
    setRanges((prev) =>
      prev.map((r, i) => (i === activeIndex ? { ...r, start, end: end ?? start } : r)),
    );
  };

  const addFlight = () => {
    const last = ranges[ranges.length - 1];
    const base = last ? last.end : dayStart(target.start_time);
    setRanges((prev) => [...prev, toLocal({ start: base, end: base }, -1)]);
    setActiveIndex(ranges.length);
    setIsInheriting(false);
  };

  /*
      Removing the last flight is allowed on every row. A row with no segments
      does not serve — that is the whole reason to do it — so this deliberately
      does not fall back to the parent's window.
  */
  const removeFlight = (index: number) => {
    const removed = ranges[index];
    if (removed && removed.id !== -1) setDeletedSegmentIds((prev) => [...prev, removed.id]);
    setRanges((prev) => prev.filter((_, i) => i !== index));
    setActiveIndex((prev) => (prev >= index && prev > 0 ? prev - 1 : prev));
  };

  const clearAllFlights = () => {
    setDeletedSegmentIds((prev) => [...prev, ...ranges.filter((r) => r.id !== -1).map((r) => r.id)]);
    setRanges([]);
    setActiveIndex(0);
  };

  const addOverride = async (subtype: string) => {
    setAddOpen(false);
    setIsSaving(true);
    setError(null);
    try {
      const created = await service.createScheduleEntry({
        name: networkLabel(subtype),
        type: entry.type,
        subtype,
        parent_id: entry.id,
        start_time: dayStart(entry.start_time),
        end_time: dayStart(entry.end_time),
      });
      setLocalOverrides((prev) => [...prev, created]);
      setTargetId(created.id);
      setListIsStale(true);
    } catch {
      setError(`Could not add the ${networkLabel(subtype)} override.`);
    } finally {
      setIsSaving(false);
    }
  };

  const removeOverride = async () => {
    if (!targetIsOverride) return;
    setIsSaving(true);
    setError(null);
    try {
      await service.deleteScheduleEntry(target.id);
      // If the deleted override is the row that was opened, there is nothing
      // left to edit here.
      if (target.id === entry.id) {
        onSaved();
        onClose();
        return;
      }
      setLocalOverrides((prev) => prev.filter((o) => o.id !== target.id));
      setTargetId(entry.id);
      setConfirmRemove(false);
      setListIsStale(true);
    } catch {
      setError("Could not remove this override.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      /*
          Segments are what actually serve: the backend only reports a row ACTIVE
          while one of its own segments covers now. So every flight is written as
          a segment — including a single one — and following the parent means one
          segment over the parent's window, not the absence of segments.
      */
      const targetRanges: Range[] =
        isInheriting && targetParent
          ? [{ start: dayStart(targetParent.start_time), end: dayStart(targetParent.end_time) }]
          : ranges.map((r) => ({ start: r.start, end: r.end }));

      if (isInheriting) {
        // The parent's window replaces whatever was there, so the old rows go.
        await Promise.all(
          (target.segments ?? []).map((s: ScheduleSegment) => service.deleteScheduleSegment(s.id)),
        );
      } else {
        await Promise.all(deletedSegmentIds.map((id) => service.deleteScheduleSegment(id)));
      }

      /*
          No flights left. The entry's own dates are not nullable so they stay as
          they are, but with no segment the row will not serve — which is the
          point of clearing them.
      */
      if (targetRanges.length === 0) {
        onSaved();
        onClose();
        return;
      }

      const span = {
        start: new Date(Math.min(...targetRanges.map((r) => r.start.getTime()))),
        end: new Date(Math.max(...targetRanges.map((r) => r.end.getTime()))),
      };

      await Promise.all(
        targetRanges.map((r, i) => {
          const existingId = isInheriting ? -1 : ranges[i].id;
          const segment = new ScheduleSegment();
          segment.id = existingId;
          segment.entry_id = target.id;
          segment.start_time = r.start;
          segment.end_time = r.end;
          return existingId === -1
            ? service.createScheduleSegment(segment)
            : service.updateScheduleSegment(existingId, segment);
        }),
      );

      await service.updateScheduleEntry(target.id, {
        start_time: span.start,
        end_time: span.end,
      });

      onSaved();
      onClose();
    } catch {
      setError("Could not save this schedule.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (listIsStale) onSaved();
    onClose();
  };

  const unusedNetworks = availableNetworks.filter(
    (n) => !localOverrides.some((o) => o.subtype?.toLowerCase() === n.toLowerCase()),
  );

  /*
      Whatever is being edited draws from the unsaved state, so the preview
      answers "where will this land" rather than "where did it used to be".
  */
  const liveRanges = (candidate: ScheduleEntry, itsParent: ScheduleEntry | null): Range[] => {
    if (candidate.id !== target.id) return rangesOf(candidate);
    if (isInheriting && itsParent) {
      return [{ start: dayStart(itsParent.start_time), end: dayStart(itsParent.end_time) }];
    }
    return ranges.map((r) => ({ start: r.start, end: r.end }));
  };

  const previewRows = [
    parent
      ? { id: parent.id, label: typeLabel(parent), ranges: rangesOf(parent), color: "#6366f1" }
      : null,
    { id: entry.id, label: typeLabel(entry), ranges: liveRanges(entry, parent), color: "#10b981" },
    ...localOverrides.map((o) => ({
      id: o.id,
      label: networkLabel(o.subtype),
      ranges: liveRanges(o, entry),
      color: networkOf(o.subtype)?.color ?? "#eab308",
    })),
  ].filter(Boolean) as { id: number; label: string; ranges: Range[]; color: string }[];

  const previewWindow = campaign.totalDays > 0 ? campaign : campaignWindow([], parent ?? entry);
  const monthLines = useMemo(() => monthBoundaries(previewWindow), [previewWindow]);

  return (
    <Modal show onHide={handleClose} centered size="lg" className="schedule-modal">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>
          {entry.name}
          {parent && (
            <span className="schedule-modal__crumb"> in {parent.name}</span>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <div className="alert alert-danger py-2 small">{error}</div>}

        {!openedAnOverride && (
        <div className="sched-targets">
          <button
            type="button"
            className={`sched-t${targetId === entry.id ? " sched-t--on" : ""}`}
            onClick={() => setTargetId(entry.id)}
          >
            All platforms
          </button>
          {localOverrides.map((override) => (
            <button
              key={override.id}
              type="button"
              className={`sched-t${targetId === override.id ? " sched-t--on" : ""}`}
              onClick={() => setTargetId(override.id)}
            >
              <i
                className={networkOf(override.subtype)?.icon}
                style={{ color: networkOf(override.subtype)?.color }}
              />
              {networkLabel(override.subtype)}
            </button>
          ))}

          {unusedNetworks.length > 0 && (
            <div className="sched-add">
              <button
                type="button"
                className="sched-t sched-t--add"
                onClick={() => setAddOpen((v) => !v)}
                disabled={isSaving}
              >
                + Platform
              </button>
              {addOpen && (
                <div className="sched-add__pop">
                  <div className="sched-add__head">Publishers on this campaign</div>
                  {unusedNetworks.map((network) => (
                    <button
                      key={network}
                      type="button"
                      className="sched-add__item"
                      onClick={() => addOverride(network)}
                    >
                      <i
                        className={socialMediaData[network.toLowerCase()]?.icon}
                        style={{ color: socialMediaData[network.toLowerCase()]?.color }}
                      />
                      {socialMediaData[network.toLowerCase()]?.label ?? network}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {targetParent && (
          <div className="sched-mode">
            <label className="sched-radio">
              <input
                type="radio"
                checked={isInheriting}
                onChange={() => setIsInheriting(true)}
              />
              <span>
                Follow {typeLabel(targetParent).toLowerCase()}
                <small>
                  {" "}
                  {formatRange({
                    start: dayStart(targetParent.start_time),
                    end: dayStart(targetParent.end_time),
                  })}
                </small>
              </span>
            </label>
            <label className="sched-radio">
              <input
                type="radio"
                checked={!isInheriting}
                onChange={() => setIsInheriting(false)}
              />
              <span>Set its own dates</span>
            </label>
          </div>
        )}

        {!isInheriting && (
          <>
            <div className="sched-picker">
              <DateRangePicker
                start={active?.start ?? null}
                end={active?.end ?? null}
                onChange={({ start, end }) => setActiveRange(start, end)}
                minDate={bounds.min}
                maxDate={bounds.max}
              />
            </div>

            <div className="sched-flights">
              <div className="sched-flights__head">
                <span>Flights</span>
                <span className="sched-chip">{ranges.length}</span>
                {ranges.length > 0 && (
                  <button
                    type="button"
                    className="sched-flights__clear ms-auto"
                    onClick={clearAllFlights}
                  >
                    Clear all
                  </button>
                )}
                <button
                  type="button"
                  className={`btn btn-sm btn-outline-primary${ranges.length > 0 ? "" : " ms-auto"}`}
                  onClick={addFlight}
                  style={{ fontSize: "0.72rem" }}
                >
                  Add flight
                </button>
              </div>
              {/* The consequence, stated where the decision is made. */}
              {ranges.length === 0 && (
                <div className="sched-flights__note sched-flights__note--warn">
                  No flights. This {typeLabel(target).toLowerCase()} will not serve until one is
                  added.
                </div>
              )}
              {ranges.map((range, index) => (
                <button
                  key={range.key}
                  type="button"
                  className={`sched-flight${index === activeIndex ? " sched-flight--on" : ""}`}
                  onClick={() => setActiveIndex(index)}
                >
                  <span className="sched-flight__dot" />
                  <span className="sched-flight__range">{formatRange(range)}</span>
                  <span className="sched-flight__days">
                    {dayCount(range.start, range.end)} days
                  </span>
                  <span
                    className="sched-flight__remove ms-auto"
                    role="button"
                    title={
                      ranges.length === 1
                        ? "Remove the last flight — this row will stop serving"
                        : "Remove this flight"
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFlight(index);
                    }}
                  >
                    Remove
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        <div className="sched-preview">
          <div className="sched-preview__head">Timeline</div>
          {previewRows.map((row) => (
            <div
              key={row.id}
              className={`sched-preview__row${
                row.id === target.id ? " sched-preview__row--active" : ""
              }`}
            >
              <span className="sched-preview__label">{row.label}</span>
              <span className="sched-track sched-track--mini">
                {monthLines.map((left, k) => (
                  <span key={`m${k}`} className="sched-track__line sched-track__line--month" style={{ left: `${left}%` }} />
                ))}
                {row.ranges.map((range, j) => {
                  const geo = barGeometry(range, previewWindow);
                  return (
                    <span
                      key={j}
                      className="sched-bar"
                      style={{
                        left: `${geo.leftPct}%`,
                        width: `${geo.widthPct}%`,
                        background: row.color,
                      }}
                    />
                  );
                })}
              </span>
            </div>
          ))}
        </div>
      </Modal.Body>

      <Modal.Footer>
        {targetIsOverride && !confirmRemove && (
          <button
            type="button"
            className="btn btn-sm btn-outline-danger me-auto"
            onClick={() => setConfirmRemove(true)}
            disabled={isSaving}
            style={{ fontSize: "0.72rem" }}
          >
            Remove override
          </button>
        )}
        {targetIsOverride && confirmRemove && (
          <div className="d-flex align-items-center gap-2 me-auto">
            <span className="text-warning" style={{ fontSize: "0.76rem" }}>
              Remove the {networkLabel(target.subtype)} override?
            </span>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={removeOverride}
              disabled={isSaving}
              style={{ fontSize: "0.72rem" }}
            >
              Confirm
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setConfirmRemove(false)}
              disabled={isSaving}
              style={{ fontSize: "0.72rem" }}
            >
              Cancel
            </button>
          </div>
        )}
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={handleClose}
          disabled={isSaving}
          style={{ fontSize: "0.72rem" }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={handleSave}
          // No flights is a valid, deliberate state — it is how a row is taken
          // out of service — so it must be savable.
          disabled={isSaving}
          style={{ fontSize: "0.72rem" }}
        >
          {isSaving ? "Saving…" : "Save"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ScheduleEntryModal;
